import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Facebook, MessageCircle } from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import ServiceAreaMap from "@/components/ServiceAreaMap";
import { trackEvent, pageContext } from "@/lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Stage 1 Remap",
  "Stage 2 Remap",
  "Stage 3 / Custom",
  "DPF / EGR / AdBlue",
  "DPF Cleaning",
  "Gearbox Tuning",
  "Diagnostics",
  "Car Key Programming",
];

const LOCATIONS = ["Nottingham", "Derby"];

export default function BookingForm() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    vehicle_make: "",
    vehicle_model: "",
    vehicle_year: "",
    service: "",
    location: "",
    preferred_date: "",
    notes: "",
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    const required = ["name", "email", "phone", "vehicle_make", "vehicle_model", "vehicle_year", "service", "location"];
    for (const k of required) {
      if (!form[k]) {
        toast.error(`Please fill in "${k.replace("_", " ")}".`);
        return;
      }
    }
    setLoading(true);
    try {
      await axios.post(`${API}/bookings`, {
        ...form,
        vehicle_year: parseInt(form.vehicle_year, 10),
      });
      toast.success("Booking received. We'll be in touch within 1 working day.");
      trackEvent("lead_form_submit", { ...pageContext(), form_name: "booking", service: form.service, fuel: form.fuel, vehicle_make: form.vehicle_make });
      setSubmitted(true);
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative bg-carbon py-24 md:py-32 px-6 md:px-10 border-t border-white/10"
      data-testid="booking-section"
    >
      <div className="max-w-[1600px] mx-auto grid grid-cols-12 gap-6">
        {/* Left */}
        <div className="col-span-12 lg:col-span-5">
          <p className="font-mono text-[11px] uppercase tracking-[0.4em] text-volt mb-6">
            ◉ Book / 07
          </p>
          <h2 className="font-display uppercase text-4xl md:text-6xl leading-[0.9] tracking-tighter mb-8">
            Book your <br />
            <span className="text-volt">remap slot.</span>
          </h2>
          <p className="font-body text-white/60 text-base md:text-lg leading-relaxed max-w-md mb-12">
            Tell us about the car. We&rsquo;ll respond within one working day
            with pricing, an available slot and the exact scope of work.
          </p>

          <div className="space-y-6 border-t border-white/10 pt-8">
            <Info label="Coverage" value="Nottingham · Derby · East Midlands" testid="loc-coverage" />
            <Info label="Service" value="100% mobile — we come to you" testid="service-type" />

            <div className="grid grid-cols-12 gap-4 py-2" data-testid="phone">
              <div className="col-span-4 font-mono text-[10px] uppercase tracking-widest text-white/40">Call / Text</div>
              <div className="col-span-8 flex flex-wrap items-center gap-3">
                <a href="tel:07523386312" className="text-volt hover:text-white transition-colors font-body font-medium" data-testid="contact-phone-link">
                  07523 386312
                </a>
                <a
                  href="https://wa.me/447523386312"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 bg-[#25D366]/15 hover:bg-[#25D366] hover:text-black text-[#25D366] font-mono text-[10px] uppercase tracking-widest px-2 py-1 transition-colors"
                  data-testid="contact-whatsapp-link"
                >
                  <MessageCircle className="w-3 h-3" strokeWidth={2} /> WhatsApp
                </a>
              </div>
            </div>

            <Info label="Hours" value="Mon–Fri · 9–5 · Sat 9:30–12" testid="hours" />
            <div className="grid grid-cols-12 gap-4 py-2" data-testid="social">
              <div className="col-span-4 font-mono text-[10px] uppercase tracking-widest text-white/40">
                Follow
              </div>
              <div className="col-span-8">
                <a
                  href="https://www.facebook.com/people/Mids-Tuning/61592316213895/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-white/90 hover:text-volt transition-colors"
                  data-testid="contact-facebook-link"
                >
                  <Facebook className="w-4 h-4" strokeWidth={1.5} />
                  facebook.com/Mids-Tuning
                </a>
              </div>
            </div>
          </div>

          <ServiceAreaMap className="mt-10" />
        </div>

        {/* Right — form */}
        <div className="col-span-12 lg:col-span-7 lg:pl-16">
          {submitted ? (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-surface-1 border border-volt p-10 md:p-14"
              data-testid="booking-success"
            >
              <div className="font-mono text-[11px] uppercase tracking-widest text-volt mb-4">
                ◉ Confirmed
              </div>
              <h3 className="font-display uppercase text-3xl md:text-4xl leading-tight mb-4">
                Thanks — booking received.
              </h3>
              <p className="text-white/60 leading-relaxed mb-8">
                We&rsquo;ll be in touch within one working day to confirm the
                slot and pricing. Sit tight.
              </p>
              <button
                onClick={() => {
                  setSubmitted(false);
                  setForm({
                    name: "",
                    email: "",
                    phone: "",
                    vehicle_make: "",
                    vehicle_model: "",
                    vehicle_year: "",
                    service: "",
                    location: "",
                    preferred_date: "",
                    notes: "",
                  });
                }}
                className="font-mono text-xs uppercase tracking-widest border border-white/20 px-6 py-3 hover:border-volt hover:text-volt transition-colors"
                data-testid="booking-new"
              >
                Book another →
              </button>
            </motion.div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-6" data-testid="booking-form">
              <div className="grid grid-cols-2 gap-6">
                <Field label="Name">
                  <input value={form.name} onChange={(e) => setField("name", e.target.value)} autoComplete="name" className={inputCls} data-testid="book-name" />
                </Field>
                <Field label="Phone">
                  <input type="tel" inputMode="tel" autoComplete="tel" value={form.phone} onChange={(e) => setField("phone", e.target.value)} className={inputCls} data-testid="book-phone" />
                </Field>
                <Field label="Email" full>
                  <input type="email" autoComplete="email" value={form.email} onChange={(e) => setField("email", e.target.value)} className={inputCls} data-testid="book-email" />
                </Field>
                <Field label="Vehicle Make">
                  <input value={form.vehicle_make} onChange={(e) => setField("vehicle_make", e.target.value)} className={inputCls} data-testid="book-make" />
                </Field>
                <Field label="Vehicle Model">
                  <input value={form.vehicle_model} onChange={(e) => setField("vehicle_model", e.target.value)} className={inputCls} data-testid="book-model" />
                </Field>
                <Field label="Year">
                  <input
                    value={form.vehicle_year}
                    onChange={(e) => setField("vehicle_year", e.target.value.replace(/\D/g, "").slice(0, 4))}
                    inputMode="numeric"
                    className={inputCls}
                    data-testid="book-year"
                  />
                </Field>
                <Field label="Preferred Date">
                  <input type="date" value={form.preferred_date} onChange={(e) => setField("preferred_date", e.target.value)} className={inputCls} data-testid="book-date" />
                </Field>

                <Field label="Service">
                  <Select value={form.service} onValueChange={(v) => setField("service", v)}>
                    <SelectTrigger
                      className="bg-transparent border-0 border-b border-white/20 rounded-none focus:border-volt focus:ring-0 h-11 text-white"
                      data-testid="book-service"
                    >
                      <SelectValue placeholder="Choose" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-1 border-white/10 text-white rounded-none">
                      {SERVICES.map((s) => (
                        <SelectItem key={s} value={s} className="focus:bg-volt focus:text-black">{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>
                <Field label="Workshop">
                  <Select value={form.location} onValueChange={(v) => setField("location", v)}>
                    <SelectTrigger
                      className="bg-transparent border-0 border-b border-white/20 rounded-none focus:border-volt focus:ring-0 h-11 text-white"
                      data-testid="book-location"
                    >
                      <SelectValue placeholder="Nottingham / Derby" />
                    </SelectTrigger>
                    <SelectContent className="bg-surface-1 border-white/10 text-white rounded-none">
                      {LOCATIONS.map((l) => (
                        <SelectItem key={l} value={l} className="focus:bg-volt focus:text-black">{l}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </Field>

                <Field label="Notes (mods, faults, goals…)" full>
                  <textarea
                    rows={3}
                    value={form.notes}
                    onChange={(e) => setField("notes", e.target.value)}
                    className={`${inputCls} resize-none`}
                    data-testid="book-notes"
                  />
                </Field>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-3 bg-volt text-black px-6 py-4 font-mono text-xs uppercase tracking-widest font-semibold hover:bg-white transition-colors duration-300 disabled:opacity-50"
                data-testid="booking-submit"
              >
                {loading ? "Sending…" : "Request booking →"}
              </button>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const inputCls =
  "w-full bg-transparent border-0 border-b border-white/20 rounded-none py-2 h-11 text-base text-white placeholder:text-white/30 focus:outline-none focus:border-volt font-body";

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : "col-span-2 md:col-span-1"}>
      <label className="font-mono text-[10px] uppercase tracking-widest text-white/50 block mb-2">{label}</label>
      {children}
    </div>
  );
}

function Info({ label, value, testid }) {
  return (
    <div className="grid grid-cols-12 gap-4 py-2" data-testid={testid}>
      <div className="col-span-4 font-mono text-[10px] uppercase tracking-widest text-white/40">
        {label}
      </div>
      <div className="col-span-8 font-body text-white/90">{value}</div>
    </div>
  );
}
